import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {setEntities, UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {GridSortItem} from '@mui/x-data-grid-pro';

import {Pagination} from '@store/common/pagination.model';
import {clientsServicesStore} from '@store/clients/services/clients-services.store';
import {
  ClientsServiceDetails,
  ClientsServicesFilters,
  ClientsServicesPagination,
  ManageClientService
} from '@store/clients/services/clients-services.model';
import {getClientsServicesDataSource} from '@store/clients/services/clients-services.requests';
import {ClientsService} from '@store/clients/services';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';

import {TableFilter} from '@store/common/tableFilter.model';
import {Array} from '@utils/extensions/array.extension';
import {TableConfig} from '@constants/table.constants';
import {ClientsContract} from '@store/clients/contracts';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';
import dayjs from 'dayjs';

export class ClientsServicesService {
  store = clientsServicesStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (clientId?: string, clientUnitId?: string, supplierId?: string) => this.store.update((state) => ({
    ...state,
    filters: {
      search: '',
      tableFilters: ClientsServicesFilters.initTableFilters(clientId, clientUnitId, supplierId),
      excludeTableFilters: ClientsServicesFilters.initExcludeTableFilters(clientId, clientUnitId, supplierId),
      filterExpiredContracts: true,
      filterExpiredServices: true
    },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  searchClientsServices = (search?: string, contractIds?: (string | number)[], filterExpiredService?: boolean): Observable<ClientsServicesPagination> => {
    return from(APIAxios({
      ...APIRoutes.GETClientsServices(),
      params: {
        contractIds: Array.formatJson(Array.mapNotNull(contractIds)),
        searchString: search || undefined,
        sortBy: 'service.clientWasteName',
        orderBy: 'ASC',
        page: 1,
        limit: TableConfig.pagination,
        filterExpiredServices: filterExpiredService ?? true,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsServicesPagination>) => {
          return response.data;
        }),
      );
  };

  getClientsServices = (contractIds: number[] = []): Observable<ClientsServicesPagination> => {
    const {
      filters: {
        search,
        tableFilters,
        excludeTableFilters,
        filterExpiredContracts,
        filterExpiredServices,
        validationStatus
      },
      sort,
      tablePagination,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    const excludeRequestFilters = TableFilter.toRequestFilters(excludeTableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsServices(),
      params: {
        contractIds: Array.formatJson(Array.mapNotNull(contractIds)),
        ...requestFilters,
        ...excludeRequestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        filterExpiredContracts,
        filterExpiredServices,
        validationStatus,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsServicesPagination>) => {
          return response.data;
        }),
        tap((clientsServicesPagination) => {
          const paginationData = Pagination.toPaginationData(clientsServicesPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(clientsServicesPagination.data),
            upsertEntitiesById(clientsServicesPagination.data.map((clientService) => clientService.id), {
              updater: (clientServiceUi) => clientServiceUi,
              creator: (clientServiceId) => ({id: clientServiceId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, clientsServicesPagination.data.map((clientsService) => clientsService.id)),
            getClientsServicesDataSource.setSuccess(),
          );
        }),
        getClientsServicesDataSource.trackRequestStatus(),
      );
  };

  getClientsServicesCSV = () => {
    const {
      filters: {search, tableFilters, filterExpiredContracts, filterExpiredServices},
      sort
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETClientsServicesCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        filterExpiredContracts: filterExpiredContracts,
        filterExpiredServices: filterExpiredServices,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'services.xlsx');
        }),
      );
  };

  createClientsService = (data: ManageClientService): Observable<ClientsService> => {
    return from(APIAxios({
      ...APIRoutes.POSTClientsService(),
      data: {
        contractId: data.contract?.id,
        supplierServiceId: data.supplierService?.id,
        clientWasteName: data.clientWasteName,
        sellPrices: data.sellPrices?.length ? data.sellPrices.map((p) => ({
          unitCostInCents: (p.unitCost || 0) * 100,
          minQuantity: p.minQuantity,
          maxQuantity: p.maxQuantity,
        })) : [],
        pctSellDiscountRule: (data.pctSellDiscountRule || 0) / 100,
        pctBuyDiscountRule: (data.pctBuyDiscountRule || 0) / 100,
        validFromDate: dayjs(data.validFromDate).set('hour', 12).toDate(),
        validToDate: dayjs(data.validToDate).set('hour', 12).toDate(),
        schedules: data.schedules || [],
        invoicingSplits: data.invoicingSplits?.length ? data.invoicingSplits.map((i) => ({
          clientOrganisationId: i.clientOrganisation?.value,
          pctSplit: (i.pctSplit || 0) / 100,
        })) : [],
        interco: data.interco ?? false,
        intersite: data.intersite ?? false,
        supplierInvoicingRequired: data.supplierInvoicingRequired ?? true,
        clientInvoicingRequired: data.clientInvoicingRequired ?? true,
        buyPriceId: data.buyPriceId,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsService>) => {
          return response.data;
        }),
        switchMap((service) => this.getClientsServices().pipe(map(() => service))),
      );
  };

  createClientServiceWithSupplier(service: any) {
    return from(APIAxios({
      ...APIRoutes.POSTClientServiceWithSupplier(),
      data: service,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error', true, err.response?.data);
        }),
        map((response: AxiosResponse<ClientsContract>) => {
          return response.data;
        })
      );
  }

  getClientServiceById = (serviceId: number | string): Observable<ManageClientService> => {
    return from(APIAxios(APIRoutes.GETClientServiceById(serviceId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsServiceDetails>) => {
          return ClientsServiceDetails.toManageClientsService(response.data);
        }),
      );
  };

  createNewPrice(serviceId: number | string, data: {
    unitCostInCents: number | undefined;
    fromDate: Date | undefined;
    unitPriceInCents: number | undefined;
  }) {
    return from(APIAxios({
      ...APIRoutes.PATCHClientServiceNewPrice(serviceId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsServiceDetails>) => {
          return ClientsServiceDetails.toManageClientsService(response.data);
        }),
        switchMap((service) => this.getClientsServices().pipe(map(() => service)))
      );
  }

  updatePrices(priceUpdate: any, progressToken: string) {
    const {
      filters: {
        search,
        tableFilters,
        excludeTableFilters,
        filterExpiredContracts,
        filterExpiredServices
      }
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    const excludeRequestFilters = TableFilter.toRequestFilters(excludeTableFilters);

    return from(APIAxios({
      ...APIRoutes.POSTClientServicePrices(),
      data: {
        ...priceUpdate,
        ...requestFilters,
        ...excludeRequestFilters,
        filterExpiredContracts: filterExpiredContracts,
        filterExpiredServices,
        searchString: search || undefined,
        progressTrackerToken: progressToken,
      },
    })).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
    );
  }

  updateServicesSettings(serviceUpdate: {
    validToDate?: Date | null;
    buyIndexationId?: number | null;
    sellIndexationId?: number | null;
    canBeRequested?: boolean;
    clientInvoicingRequired?: boolean;
    supplierInvoicingRequired?: boolean;
  }, progressToken: string) {
    const {
      filters: {
        search,
        tableFilters,
        excludeTableFilters,
        filterExpiredContracts,
        filterExpiredServices,
      },
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    const excludeRequestFilters = TableFilter.toRequestFilters(excludeTableFilters);

    return from(APIAxios({
      ...APIRoutes.PATCHClientServicesSettings(),
      data: {
        ...serviceUpdate,
        ...requestFilters,
        ...excludeRequestFilters,
        filterExpiredContracts,
        filterExpiredServices,
        searchString: search || undefined,
        progressTrackerToken: progressToken,
      },
    })).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
    );
  }

  updateWasteContainerForContainerId(contractId: number | string, containerId?: number, wasteId?: number) {
    return from(
      APIAxios({
        ...APIRoutes.PATCHWasteContainer(contractId),
        data: {
          containerId,
          wasteId,
        }
      })
    )
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  }

  updateClientServiceById = (serviceId: string | number, data: ManageClientService): Observable<ManageClientService> => {
    return from(APIAxios({
      ...APIRoutes.PATCHClientServiceById(serviceId),
      data: {
        clientWasteName: data.clientWasteName,
        pctSellDiscountRule: (data.pctSellDiscountRule || 0) / 100,
        pctBuyDiscountRule: (data.pctBuyDiscountRule || 0) / 100,
        validFromDate: dayjs(data.validFromDate).set('hour', 12).toDate(),
        validToDate: data.validToDate ? dayjs(data.validToDate).set('hour', 12).toDate() : null,
        invoicingSplits: data.invoicingSplits?.length ? data.invoicingSplits.map((i) => ({
          clientOrganisationId: i.clientOrganisation?.value,
          pctSplit: (i.pctSplit || 0) / 100,
        })) : [],
        schedulesToAdd: data.schedules?.filter((sch) => sch.id == null),
        schedulesToUpdate: data.schedules?.filter((sch) => !!sch.id && sch.wasUpdated),
        schedulesToDelete: data.schedulesToDelete,
        canBeRequested: data.canBeRequested ?? false,
        interco: data.interco ?? false,
        intersite: data.intersite ?? false,
        supplierInvoicingRequired: data.supplierInvoicingRequired ?? true,
        clientInvoicingRequired: data.clientInvoicingRequired ?? true,
        distributionRules: data.distributionRules || [],
        datahubSpec: {
          idDatahub: data.datahubSpec?.idDatahub,
        },
        execUnitName: data.execUnitName,
        validationStatus: data.validationStatus,
        buyIndexationId: data.buyIndexationId,
        sellIndexationId: data.sellIndexationId,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientsServiceDetails>) => {
          return ClientsServiceDetails.toManageClientsService(response.data);
        }),
        switchMap((service) => this.getClientsServices().pipe(map(() => service))),
      );
  };

  deleteClientsServiceInList = (clientsServiceId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEClientServiceById(clientsServiceId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  setSelectedClientsServices = (clientsServiceIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (clientsServiceUI) => ({
          ...clientsServiceUI,
          selected: clientsServiceIds.includes(clientsServiceUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  updateKind(serviceId: number, newKindId: number, fromDate: Date) {
    return from(APIAxios({
      ...APIRoutes.PATCHClientServiceKind(serviceId),
      data: {
        kindId: newKindId,
        fromDate,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap((service) => this.getClientsServices().pipe(map(() => service))),
      );
  }

  resetEntities() {
    this.store.update(
      setEntities([]),
    );
  }
}

export const clientsServicesService = new ClientsServicesService();
